import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';

interface ZatcaSubmissionStatusProps {
  invoice: any;
  page?: any;
  onStatusChange?: () => void;
}

interface SubmissionLog {
  _id: string;
  action: string;
  status: string;
  createdAt: string;
  errorDetails?: string[];
  processingTime?: number;
}

const ZatcaSubmissionStatus: React.FC<ZatcaSubmissionStatusProps> = ({ invoice, page, onStatusChange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState<SubmissionLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // التحقق من تفعيل المرحلة الثانية
  // استثناء لفاتير مرتجع استيراد: السماح بالعرض حتى لو كانت مسودة
  const isImportReturn = page?.type === 'import_invoice_return' || invoice?.type === 'import_invoice_return';
  if (!invoice?.zatcaPhase2?.enabled && invoice?.status !== 'completed' && !isImportReturn) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'success';
      case 'rejected':
      case 'failed':
      case 'validation_failed':
      case 'error':
        return 'error';
      case 'submitted':
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleIcon />;
      case 'rejected':
      case 'failed':
        return <ErrorIcon />;
      case 'submitted':
        return <PendingIcon />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'مقبولة';
      case 'rejected':
        return 'مرفوضة';
      case 'failed':
        return 'فشل الإرسال';
      case 'validation_failed':
        return 'فشل التحقق';
      case 'error':
        return 'خطأ في الإرسال';
      case 'submitted':
        return 'قيد المراجعة';
      case 'warning':
        return 'تحذير';
      case 'pending':
      default:
        return 'لم يتم الإرسال';
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const response = await axios.post(`/api/invoices/${invoice._id}/zatca/submit`);
      
      if (response.data.success) {
        setSuccess('تم إرسال الفاتورة بنجاح');
        if (onStatusChange) {
          onStatusChange();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'حدث خطأ أثناء الإرسال');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const response = await axios.post(`/api/invoices/${invoice._id}/zatca/retry`);
      
      if (response.data.success) {
        setSuccess('تم إعادة إرسال الفاتورة بنجاح');
        if (onStatusChange) {
          onStatusChange();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'حدث خطأ أثناء إعادة المحاولة');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShowLogs = async () => {
    try {
      setLoadingLogs(true);
      const response = await axios.get(`/api/invoices/${invoice._id}/zatca/logs`);
      setLogs(response.data.data || []);
      setShowLogs(true);
    } catch (err: any) {
      setError('فشل تحميل السجلات');
    } finally {
      setLoadingLogs(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ar-SA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const submissionStatus = invoice.zatcaPhase2?.submissionStatus || 'pending';
  // استثناء لفاتير مرتجع استيراد: السماح بالارسال حتى لو كانت الحالة مسودة
  const canSubmit = (invoice.status === 'completed' || isImportReturn) && submissionStatus === 'pending';
  // السماح بإعادة المحاولة للفواتير الفاشلة أو المرفوضة، بغض النظر عن حالة الفاتورة
  const canRetry = (submissionStatus === 'failed' || submissionStatus === 'rejected' ||
                    submissionStatus === 'validation_failed' || submissionStatus === 'error') &&
                   (invoice.zatcaPhase2?.retryCount || 0) < 3;

  return (
    <Card sx={{ mt: 3, border: '2px solid', borderColor: 'primary.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            🔒 حالة الإرسال إلى هيئة الزكاة
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* الحالة */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 'bold' }}>
              الحالة:
            </Typography>
            <Chip
              icon={getStatusIcon(submissionStatus)}
              label={getStatusText(submissionStatus)}
              color={getStatusColor(submissionStatus)}
              size="medium"
            />
          </Box>

          {/* UUID */}
          {invoice.zatcaPhase2?.uuid && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 'bold' }}>
                UUID:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: 'monospace', 
                  bgcolor: 'grey.100', 
                  p: 1, 
                  borderRadius: 1,
                  fontSize: '0.85rem'
                }}
              >
                {invoice.zatcaPhase2.uuid}
              </Typography>
            </Box>
          )}

          {/* تاريخ الإرسال */}
          {invoice.zatcaPhase2?.submissionDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 'bold' }}>
                تاريخ الإرسال:
              </Typography>
              <Typography variant="body2">
                {formatDate(invoice.zatcaPhase2.submissionDate)}
              </Typography>
            </Box>
          )}

          {/* عدد المحاولات */}
          {invoice.zatcaPhase2?.retryCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 'bold' }}>
                عدد المحاولات:
              </Typography>
              <Chip 
                label={invoice.zatcaPhase2.retryCount} 
                size="small"
                color={invoice.zatcaPhase2.retryCount >= 3 ? 'error' : 'default'}
              />
            </Box>
          )}

          {/* رمز الاستجابة */}
          {invoice.zatcaPhase2?.responseCode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 'bold' }}>
                رمز الاستجابة:
              </Typography>
              <Chip
                label={invoice.zatcaPhase2.responseCode}
                size="small"
                color={invoice.zatcaPhase2.responseCode === '200' ? 'success' : 'error'}
                sx={{ fontFamily: 'monospace' }}
              />
            </Box>
          )}

          {/* الأخطاء */}
          {invoice.zatcaPhase2?.validationErrors && invoice.zatcaPhase2.validationErrors.length > 0 && (
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" fontSize="small" />
                الأخطاء ({invoice.zatcaPhase2.validationErrors.length}):
              </Typography>
              <Box sx={{ bgcolor: 'error.lighter', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                {invoice.zatcaPhase2.validationErrors.map((error: any, index: number) => {
                  // التعامل مع الأخطاء سواء كانت string أو object
                  if (typeof error === 'string') {
                    return (
                      <Typography key={index} variant="body2" color="error" sx={{ mb: 1, fontSize: '0.85rem' }}>
                        • {error}
                      </Typography>
                    );
                  }
                  
                  // عرض تفاصيل الخطأ الكاملة
                  return (
                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < invoice.zatcaPhase2.validationErrors.length - 1 ? '1px solid' : 'none', borderColor: 'error.light' }}>
                      {(error.errorType || error.type) && (
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main', mb: 0.5 }}>
                          التصنيف: {error.errorType || error.type}
                        </Typography>
                      )}
                      {error.code && (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'error.dark', mb: 0.5 }}>
                          الرمز: {error.code}
                        </Typography>
                      )}
                      {error.category && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                          الفئة: {error.category}
                        </Typography>
                      )}
                      {error.message && (
                        <Typography variant="body2" sx={{ color: 'error.dark', mt: 1 }}>
                          الرسالة: {error.message}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* التحذيرات */}
          {invoice.zatcaPhase2?.validationWarnings && invoice.zatcaPhase2.validationWarnings.length > 0 && (
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" fontSize="small" />
                التحذيرات ({invoice.zatcaPhase2.validationWarnings.length}):
              </Typography>
              <Box sx={{ bgcolor: 'warning.lighter', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                {invoice.zatcaPhase2.validationWarnings.map((warning: any, index: number) => {
                  // التعامل مع التحذيرات سواء كانت string أو object
                  if (typeof warning === 'string') {
                    return (
                      <Typography key={index} variant="body2" color="warning.dark" sx={{ mb: 1, fontSize: '0.85rem' }}>
                        • {warning}
                      </Typography>
                    );
                  }
                  
                  // عرض تفاصيل التحذير الكاملة
                  return (
                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < invoice.zatcaPhase2.validationWarnings.length - 1 ? '1px solid' : 'none', borderColor: 'warning.light' }}>
                      {(warning.warningType || warning.type) && (
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark', mb: 0.5 }}>
                          التصنيف: {warning.warningType || warning.type}
                        </Typography>
                      )}
                      {warning.code && (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'warning.dark', mb: 0.5 }}>
                          الرمز: {warning.code}
                        </Typography>
                      )}
                      {warning.category && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                          الفئة: {warning.category}
                        </Typography>
                      )}
                      {warning.message && (
                        <Typography variant="body2" sx={{ color: 'warning.dark', mt: 1 }}>
                          الرسالة: {warning.message}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* الأزرار */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            {canSubmit && (
              <Button
                variant="contained"
                color="primary"
                startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSubmit}
                disabled={submitting}
              >
                إرسال للهيئة
              </Button>
            )}

            {canRetry && (
              <Button
                variant="contained"
                color="warning"
                startIcon={submitting ? <CircularProgress size={20} /> : <RefreshIcon />}
                onClick={handleRetry}
                disabled={submitting}
              >
                إعادة المحاولة
              </Button>
            )}

            <Button
              variant="outlined"
              startIcon={loadingLogs ? <CircularProgress size={20} /> : <HistoryIcon />}
              onClick={handleShowLogs}
              disabled={loadingLogs}
            >
              عرض السجلات
            </Button>
          </Box>
        </Box>
      </CardContent>

      {/* Dialog السجلات */}
      <Dialog open={showLogs} onClose={() => setShowLogs(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">سجلات الإرسال</Typography>
            <IconButton onClick={() => setShowLogs(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {logs.length === 0 ? (
            <Alert severity="info">لا توجد سجلات</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>العملية</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>الوقت (ms)</TableCell>
                    <TableCell>التفاصيل</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>{formatDate(log.createdAt)}</TableCell>
                      <TableCell>
                        <Chip label={log.action} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={log.status} 
                          size="small"
                          color={log.status === 'success' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{log.processingTime || '-'}</TableCell>
                      <TableCell>
                        {log.errorDetails && log.errorDetails.length > 0 ? (
                          <Tooltip title={log.errorDetails.join(', ')}>
                            <ErrorIcon color="error" fontSize="small" />
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogs(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ZatcaSubmissionStatus;